/** @jsxImportSource solid-js */
import { createEffect, For, Show, type JSX } from "solid-js";
import { useWidgetStore } from "../hooks/widget-context";
import { popIn, popOut } from "../utils/widget-motion";
import { WidgetIcon } from "./widget-icon";

/**
 * Bottom-center notification stack: each toast slides in from the bottom and
 * pushes the older ones up; dismissal fades it out and the stack settles.
 */
export function WidgetToast(): JSX.Element {
  const store = useWidgetStore();
  return (
    <div
      data-pl-toasts=""
      class="z-pl pointer-events-none fixed bottom-[max(1.5rem,env(safe-area-inset-bottom,0px))] left-1/2 flex w-max max-w-[calc(100vw-2.5rem)] -translate-x-1/2 flex-col items-center gap-2 font-sans"
    >
      <For each={store.toasts()}>
        {(toast) => {
          let rowEl: HTMLDivElement | undefined;
          let exitStarted = false;
          // popOut (an animation) outranks any inline style the entrance left
          // behind; a class-based fade loses to motion's committed opacity.
          // The guard keeps unrelated set changes from restarting the exit.
          createEffect(() => {
            if (store.leavingToastIds().has(toast.id) && !exitStarted) {
              exitStarted = true;
              const el = rowEl;
              // The exit animation owns the removal timing - no duplicated
              // duration constant in the store.
              if (el) void popOut(el).then(() => store.removeToast(toast.id));
              else store.removeToast(toast.id);
            }
          });
          return (
            <div
              ref={(el) => {
                rowEl = el;
                popIn(el, { origin: "bottom center", from: "translateY(16px) scale(0.97)" });
              }}
              data-pl-toast=""
              classList={{
                "rounded-pl border-pl-border bg-pl-background text-pl-foreground shadow-pl pointer-events-auto flex max-w-full cursor-pointer items-center gap-2.5 border px-4 py-2.5 text-sm": true,
                "pointer-events-none": store.leavingToastIds().has(toast.id),
              }}
              onClick={() => store.dismissToast(toast.id)}
            >
              <span
                classList={{
                  "grid h-5 w-5 shrink-0 place-items-center rounded-full": true,
                  "bg-pl-success text-white": toast.variant === "ok",
                  "bg-pl-destructive text-white": toast.variant === "err",
                  "bg-pl-foreground/[0.08] text-pl-muted-foreground": toast.variant === "pending",
                }}
              >
                <Show when={toast.variant === "pending"}>
                  <span class="animate-pl-spin block h-3 w-3 rounded-full border-2 border-current border-r-transparent" />
                </Show>
                <Show when={toast.variant === "ok"}>
                  <WidgetIcon name="check" size={12} strokeWidth={3} />
                </Show>
                <Show when={toast.variant === "err"}>
                  <WidgetIcon name="x" size={12} />
                </Show>
              </span>
              <span>
                {toast.message}
                <Show when={toast.detail}>
                  <small class="text-pl-muted-foreground mt-px block text-xs">{toast.detail}</small>
                </Show>
              </span>
            </div>
          );
        }}
      </For>
    </div>
  );
}
